import type { CheckItem, Section } from '../types';
import { displayItemIndices } from './sectionItemSort';

/** One checklist row as plain text (no dates or checkbox markers). */
export function formatItemLine(item: CheckItem): string {
  return item.text.trim().replace(/\s+/g, ' ');
}

/** Section items as `- line` blocks for SMS / iMessage. */
export function formatSectionListForClipboard(section: Section, opts?: { includeTitle?: boolean }): string {
  const includeTitle = opts?.includeTitle !== false;
  const lines: string[] = [];

  if (includeTitle && section.title.trim()) {
    lines.push(section.title.trim());
  }

  for (const ii of displayItemIndices(section)) {
    const item = section.items[ii]!;
    const body = formatItemLine(item);
    if (!body) continue;
    lines.push(`- ${body}`);
  }

  if (lines.length === 0) return includeTitle ? section.title.trim() : '';
  if (!includeTitle && lines.every(l => l.startsWith('- '))) return lines.join('\n');
  return lines.join('\n');
}

/** All sections on a page, separated by blank lines. */
export function formatPageListsForClipboard(
  sections: Section[],
  opts?: { includeTitles?: boolean }
): string {
  const includeTitles = opts?.includeTitles !== false;
  const blocks = sections
    .map(s => formatSectionListForClipboard(s, { includeTitle: includeTitles }))
    .filter(b => b.length > 0);
  return blocks.join('\n\n');
}

export async function copyTextToClipboard(text: string): Promise<void> {
  if (!text.trim()) throw new Error('Nothing to copy');
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(ta);
  if (!ok) throw new Error('Could not copy to clipboard');
}
